import { useTranslation } from "react-i18next";
import { useScrollAnimation } from "../../../hooks/useScrollAnimation";

interface CardMeta {
  id: string;
  icon: string;
  target: string;
  highlightIdx: number;
  featureCount: number;
}

const CARDS: CardMeta[] = [
  { id: "c0", icon: "ri-layout-4-line",   target: "custom-website", highlightIdx: 4, featureCount: 5 },
  { id: "c1", icon: "ri-stack-line",       target: "modules",        highlightIdx: -1, featureCount: 4 },
  { id: "c2", icon: "ri-pantone-line",     target: "brand",          highlightIdx: -1, featureCount: 4 },
  { id: "c3", icon: "ri-search-eye-line",  target: "services",       highlightIdx: -1, featureCount: 4 },
];

const QA_ICONS = [
  "ri-bug-2-line",
  "ri-shield-keyhole-line",
  "ri-speed-up-line",
  "ri-team-line",
];

export default function ServicesSection() {
  const { t } = useTranslation();
  const { ref, isVisible } = useScrollAnimation(0.1);
  const { ref: qaRef, isVisible: qaVis } = useScrollAnimation(0.1);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="services" className="py-28 bg-[#0C0C0C]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div ref={ref} className="text-center mb-16">
          <span className={`text-[#C8953A] text-sm font-semibold tracking-widest uppercase ${isVisible ? "anim-fade-up" : "opacity-0"}`}>
            {t("svc.eyebrow")}
          </span>
          <h2 className={`text-4xl xl:text-5xl font-bold text-white mt-3 mb-5 leading-tight ${isVisible ? "anim-fade-up anim-d-100" : "opacity-0"}`}>
            {t("svc.title")}
          </h2>
          <p className={`text-white/45 text-lg max-w-lg mx-auto leading-relaxed ${isVisible ? "anim-fade-up anim-d-200" : "opacity-0"}`}>
            {t("svc.sub")}
          </p>
        </div>

        {/* Cards — 4 columns, no images */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 mb-10">
          {CARDS.map((card, i) => (
            <div
              key={card.id}
              className={`group bg-[#161616] border border-white/8 rounded-3xl overflow-hidden cursor-pointer hover:border-[#C8953A]/30 transition-all duration-400 hover:-translate-y-1 ${isVisible ? `anim-fade-up anim-d-${(i + 2) * 100}` : "opacity-0"}`}
            >
              <div className="p-7">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#C8953A]/10 group-hover:bg-[#C8953A]/20 transition-colors duration-300">
                    <i className={`${card.icon} text-[#C8953A] text-lg`} />
                  </div>
                  <span className="text-white/30 text-xs font-medium tracking-widest uppercase">
                    {t(`svc.${card.id}.label`)}
                  </span>
                </div>
                <h3 className="text-white font-bold text-lg mb-3">{t(`svc.${card.id}.title`)}</h3>
                <p className="text-white/45 text-sm leading-relaxed mb-5">{t(`svc.${card.id}.desc`)}</p>

                {/* Feature tags */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {Array.from({ length: card.featureCount }, (_, j) => {
                    const isHighlight = j === card.highlightIdx;
                    return (
                      <span
                        key={j}
                        className={`text-xs rounded-full px-3 py-1 border transition-colors duration-300 ${
                          isHighlight
                            ? "text-[#C8953A] bg-[#C8953A]/10 border-[#C8953A]/30 font-semibold"
                            : "text-white/40 bg-white/5 border-white/8"
                        }`}
                      >
                        {isHighlight && <i className="ri-shield-check-line mr-1 text-[10px]" />}
                        {t(`svc.${card.id}.f${j}`)}
                      </span>
                    );
                  })}
                </div>

                <button
                  onClick={() => scrollTo(card.target)}
                  className="whitespace-nowrap flex items-center gap-2 text-[#C8953A] text-sm font-semibold group-hover:gap-3 transition-all duration-200 cursor-pointer"
                >
                  {t(`svc.${card.id}.cta`)}
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-sm" />
                  </div>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* QA Guarantee Cards */}
        <div ref={qaRef} className={`${qaVis ? "anim-fade-up" : "opacity-0"}`}>
          <div
            className="border border-[#C8953A]/15 rounded-3xl overflow-hidden p-8 xl:p-12"
            style={{ background: "linear-gradient(135deg, rgba(200,149,58,0.06) 0%, rgba(200,149,58,0.02) 100%)" }}
          >
            {/* Section Header */}
            <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-10">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-[#C8953A]/15 border border-[#C8953A]/25 flex-shrink-0">
                  <i className="ri-verified-badge-line text-[#C8953A] text-xl" />
                </div>
                <div>
                  <div className="text-[#C8953A] text-xs font-semibold tracking-widest uppercase mb-1">
                    {t("svc.qa.eyebrow")}
                  </div>
                  <h3 className="text-white font-bold text-xl leading-snug">
                    {t("svc.qa.title")}
                  </h3>
                </div>
              </div>
              <p className="text-white/40 text-sm leading-relaxed max-w-xs xl:text-right flex-shrink-0">
                {t("svc.qa.sub")}
              </p>
            </div>

            {/* QA Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              {QA_ICONS.map((icon, i) => (
                <div
                  key={i}
                  className={`group bg-[#161616] border border-white/8 rounded-2xl p-6 hover:border-[#C8953A]/30 hover:-translate-y-1 transition-all duration-300 ${qaVis ? `anim-fade-up anim-d-${(i + 1) * 100}` : "opacity-0"}`}
                >
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#C8953A]/10 border border-[#C8953A]/15 mb-5 group-hover:bg-[#C8953A]/20 transition-colors duration-300">
                    <i className={`${icon} text-[#C8953A] text-lg`} />
                  </div>
                  <h4 className="text-white font-bold text-base mb-2">{t(`svc.qa${i}.title`)}</h4>
                  <p className="text-white/40 text-xs leading-relaxed">{t(`svc.qa${i}.text`)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
